import { useFormikContext } from "formik";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";
import { marked } from "marked";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { useEditor } from "@tiptap/react";
import { dispatch } from "../redux/store";
import { setStreaming, setCredentials } from "../redux/slices/authSlice";
import { connectSocket } from "../sockets/socket";
import { debugSSEConnection, logSSEEvent } from "../utils/sseDebugger";
import { getSessionUUID } from "../utils/uuidManager";
const WebSocketContext = createContext();

export const WebSocketProvider = ({ editor, children }) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state?.auth.userInfo);
  const [ws, setWs] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const formikProps = useFormikContext();

  const editorRef = useRef(null);
  const [openOutlineRewrite, setOpenOutlineRewrite] = useState(false);

  const introHeading = useRef("");
  const [openIntroductionModal, setOpenIntroductionModal] = useState(false);
  const [openIntroductionRewrite, setOpenIntroductionRewrite] = useState(false);
  const [openRephraseModal, setOpenRephraseModal] = useState(false);

  const [partData, setPartData] = useState(null);
  const [openPartModal, setOpenPartModal] = useState(false);
  const [openPartRewrite, setOpenPartRewrite] = useState(false);

  const [customData, setCustomData] = useState(null);
  const [openCustomModal, setOpenCustomModal] = useState(false);

  const concluHeading = useRef("");
  const [openConclusionModal, setOpenConclusionModal] = useState(false);
  const [openConclusionRewrite, setOpenConclusionRewrite] = useState(false);
  const [analysisState, setAnalysisState] = useState();
  const [uploadingState, setUploadingState] = useState();
  const bufferRef = useRef(""); // Buffer to accumulate chunks
  const counter = useRef(0);

  const tempEditor = useEditor({
    content: "",
    extensions: [
      StarterKit,
      Table.configure({
        resizable: true,
        allowTableNodeSelection: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
  });

  const validateContent = (content) => {
    try {
      if (!content || typeof content !== "string") return false;
      // Remove any empty React fragments anywhere in content:
      const cleanedContent = content
        .replace(/<>\s*<\/>/g, "") // empty fragments
        .replace(/<>\s*/g, "") // opening fragment without closing
        .replace(/\s*<\/>/g, "") // closing fragment without opening
        .replace(/<\/?React.Fragment>/g, "") // just in case fragments are serialized differently
        .replace(/<li>\s*<\/li>/g, "<li> </li>");

      tempEditor.commands.clearContent();
      tempEditor.commands.insertContent(cleanedContent);
      return true;
    } catch (error) {
      // console.error("Invalid content:", error);
      // console.log("Problematic HTML content:\n", content);

      return false;
    }
  };

  const reconnectSSE = () => {
    if (ws) {
      ws.close();
      setWs(null);
    }
    setIsConnecting(false);
    // The useEffect will handle creating a new connection
  };

  const cleanGeneratedContent = (content) => {
  const originalContent = content;
console.log("Cleaning generated content. Original length:", originalContent);
  // Step 0: Normalize markdown block start and end
  content = content.replace(/^`{0,3}h?t?m?l?/i, "");
  content = content.replace(/`{1,3}$/, "");

  // Step 1: Convert markdown to HTML
  content = marked(content);

  // Step 2: Remove unnecessary spaces/newlines between tags
  content = content.replace(/>[\s\n\r]+</g, "><");

  // ✅ Step 3: Remove "Nombre de mots : ..." or "Word count : ..." only if at the end
  content = content
    // Case 1: Plain text at the end
    .replace(/(?:Nombre\s*de\s*mots?\s*:?\s*\d+|Word\s*count\s*:?\s*\d+)\s*$/i, "")
    // Case 2: Inside <p>, <div>, <strong>, or <span> tags (possibly nested) at the end
    .replace(
      /<(?:p|div|strong|span)[^>]*>\s*(?:<[^>]+>\s*)?(?:Nombre\s*de\s*mots?\s*:?\s*\d+|Word\s*count\s*:?\s*\d+)\s*(?:<\/[^>]+>\s*)?<\/(?:p|div|strong|span)>\s*$/i,
      ""
    )
    // Case 3: If the above is followed only by empty <br> or whitespace at the very end
    .replace(
      /<(?:p|div|strong|span)[^>]*>\s*(?:<[^>]+>\s*)?(?:Nombre\s*de\s*mots?\s*:?\s*\d+|Word\s*count\s*:?\s*\d+)\s*(?:<\/[^>]+>\s*)?<\/(?:p|div|strong|span)>(?:\s|<br\s*\/?>)*$/i,
      ""
    );

  // Step 4: Sanitize HTML
  let sanitizedHtml = DOMPurify.sanitize(content, {
    USE_PROFILES: { html: true },
  });

  // Step 5: Remove invalid React fragments (<> </>)
  sanitizedHtml = sanitizedHtml.replace(/<>\s*<\/>/g, "");

  // Step 6: Replace empty <li> tags
  sanitizedHtml = sanitizedHtml.replace(/<li>\s*<\/li>/g, "<li> </li>");

  // Step 7: Fix empty/faulty table structures
  sanitizedHtml = sanitizedHtml.replace(
    /<table>\s*<\/table>/g,
    `<table><thead><tr><th> </th></tr></thead><tbody><tr><td> </td></tr></tbody></table>`
  );
  sanitizedHtml = sanitizedHtml.replace(
    /<thead>\s*<\/thead>/g,
    `<thead><tr><th> </th></tr></thead>`
  );
  sanitizedHtml = sanitizedHtml.replace(
    /<tbody>\s*<\/tbody>/g,
    `<tbody><tr><td> </td></tr></tbody>`
  );
  sanitizedHtml = sanitizedHtml.replace(/<tr>\s*<\/tr>/g, `<tr><td> </td></tr>`);
  sanitizedHtml = sanitizedHtml.replace(/<(td|th)>\s*<\/\1>/g, "<$1> </$1>");

  // Step 8: Clean excessive spacing
  sanitizedHtml = sanitizedHtml.replace(/\n\s{2,}/g, "");
  sanitizedHtml = sanitizedHtml.replace(/>[\n]+</g, "><");

  // Step 9: Remove leading/trailing empty elements
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = sanitizedHtml;

  const allElements = Array.from(tempDiv.querySelectorAll("*"));

  // Remove leading empty elements
  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const tagName = element.tagName.toLowerCase();
    if ((tagName === "p" || tagName === "div") && !element.textContent.trim()) {
      element.remove();
    } else if (
      tagName === "br" &&
      (!element.previousSibling ||
        (element.previousSibling.nodeType === Node.TEXT_NODE &&
          !element.previousSibling.textContent.trim()))
    ) {
      element.remove();
    } else {
      break;
    }
  }

  // Remove trailing empty elements
  const remainingElements = Array.from(tempDiv.querySelectorAll("*"));
  for (let i = remainingElements.length - 1; i >= 0; i--) {
    const element = remainingElements[i];
    const tagName = element.tagName.toLowerCase();
    if ((tagName === "p" || tagName === "div") && !element.textContent.trim()) {
      element.remove();
    } else if (
      tagName === "br" &&
      (!element.nextSibling ||
        (element.nextSibling.nodeType === Node.TEXT_NODE &&
          !element.nextSibling.textContent.trim()))
    ) {
      element.remove();
    } else {
      break;
    }
  }

  // Remove leading/trailing whitespace text nodes
  const childNodes = Array.from(tempDiv.childNodes);
  for (let i = 0; i < childNodes.length; i++) {
    const node = childNodes[i];
    if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
      node.remove();
    } else {
      break;
    }
  }

  const remainingChildNodes = Array.from(tempDiv.childNodes);
  for (let i = remainingChildNodes.length - 1; i >= 0; i--) {
    const node = remainingChildNodes[i];
    if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
      node.remove();
    } else {
      break;
    }
  }

  // Step 10: Final cleanup and return
  sanitizedHtml = tempDiv.innerHTML.trim();
  sanitizedHtml = sanitizedHtml.replace(/^\s+|\s+$/g, "");

  return sanitizedHtml;
};


  // Ensure UUID is set in userInfo as soon as user is available
  useEffect(() => {
    if (userInfo && userInfo.unique_id && !userInfo.uuid) {
      const uuid = getSessionUUID();
      dispatch(
        setCredentials({
          userInfo: {
            ...userInfo,
            uuid,
          },
          accessToken,
        })
      );
    }
  }, [userInfo?.unique_id, accessToken]);

  useEffect(() => {
    let socket = null;

    try {
      if (userInfo && userInfo.unique_id && formikProps.values.id && !ws && !isConnecting) {
        // Debug SSE connection parameters
        if (!debugSSEConnection(userInfo, formikProps.values.id)) {
          return;
        }

        setIsConnecting(true);
        const uuid = getSessionUUID();
        dispatch(
          setCredentials({
            userInfo: {
              ...userInfo,
              uuid,
            },
            accessToken,
          })
        );
        socket = connectSocket(userInfo.unique_id, formikProps.values.id, uuid);
        setWs(socket);

        socket.onopen = () => {
          setIsConnecting(false);
        };

        socket.onerror = (error) => {
          setIsConnecting(false);
          dispatch(setStreaming({ isStreaming: false }));
        };

        socket.onclose = () => {
          setIsConnecting(false);
          setWs(null);
          dispatch(setStreaming({ isStreaming: false }));
        };

        socket.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            logSSEEvent("message_received", { type: data.type, contentType: data.contentType });

            // Set streaming state for all content types
            dispatch(
              setStreaming({
                isStreaming: true,
              })
            );

            if (data.type === "analysis") {
              setAnalysisState(t(data?.message));
            } else if (data.type === "uploading") {
              setUploadingState(t(data?.message));
            } else if (data.type === "stream_start") {
              // Reset buffer when new stream starts
              bufferRef.current = "";
              counter.current = 0;
              logSSEEvent("stream_start", { contentType: data.contentType });
            } else if (data.type === "stream_end") {
              // Stream ended, stop streaming state
              dispatch(setStreaming({ isStreaming: false }));
              logSSEEvent("stream_end", { contentType: data.contentType });
            } else {
              // Handle streaming data
              if (data.data) {
                bufferRef.current = bufferRef.current + data.data;
                counter.current = counter.current + 1;
              }

              try {
                // Handle streaming content updates in real-time
                const shouldUpdate = data.data !== "" && (
                  bufferRef.current.length > 0 ||
                  data.type === "stream_chunk" ||
                  validateContent(bufferRef.current)
                );

                if (shouldUpdate) {
                  if (data.contentType === "outline") {
                    if (data.isRewrite) {
                      setOpenOutlineRewrite(true);
                      formikProps.setFieldValue(
                        "outline_rewrite",
                        cleanGeneratedContent(bufferRef.current)
                      );
                    } else {
                      // Update outline in real-time
                      if (editorRef.current) {
                        editorRef.current.commands.setContent(
                          cleanGeneratedContent(bufferRef.current)
                        );
                      }
                    }
                  } else if (data.contentType === "introduction") {
                    if (data.isRewrite) {
                      setOpenIntroductionRewrite(true);
                      formikProps.setFieldValue(
                        "introduction_rewrite",
                        cleanGeneratedContent(bufferRef.current)
                      );
                    } else {
                      // Close modal and update introduction zone in real-time
                      setOpenIntroductionModal(false);
                      const zone = document.getElementById("introzone");
                      if (zone) {
                        zone.innerHTML = cleanGeneratedContent(
                          introHeading.current + bufferRef.current
                        );
                      }
                    }
                  } else if (data.contentType === "part") {
                    // Append stream data safely (ignore undefined/empty chunks)
                    if (typeof data.text === "string" && data.text.trim() !== "") {
                      bufferRef.current += data.text;
                    }

                    const cleaned = cleanGeneratedContent(bufferRef.current);

                    // Skip if buffer is still invalid or contains only "undefined"
                    if (
                      !cleaned ||
                      cleaned.trim() === "" ||
                      cleaned.includes("undefinedundefined")
                    ) {
                      return;
                    }

                    if (data.isRewrite) {
                      // Check if current part has paragraph content to determine behavior
                      const hasContentWithParagraphs = () => {
                        if (!partData?.content) return true; // Default to showing modal if no content info

                        try {
                          // Create a temporary div to parse HTML more reliably across browsers
                          const tempDiv = document.createElement('div');
                          tempDiv.innerHTML = partData.content;

                          // Check for paragraph elements specifically
                          const paragraphs = tempDiv.querySelectorAll('p');
                          if (paragraphs.length > 0) {
                            // Check if any paragraph has meaningful content
                            for (let p of paragraphs) {
                              if (p.textContent && p.textContent.trim().length > 0) {
                                return true;
                              }
                            }
                          }

                          // Also check for other content elements that aren't headings
                          const contentElements = tempDiv.querySelectorAll('div, span, li, ul, ol, blockquote, table, td, th');
                          for (let element of contentElements) {
                            if (element.textContent && element.textContent.trim().length > 0) {
                              return true;
                            }
                          }

                          // Check for any text nodes that aren't in headings
                          const allElements = tempDiv.querySelectorAll('*');
                          for (let element of allElements) {
                            const tagName = element.tagName.toLowerCase();
                            if (
                              !tagName.startsWith('h') &&
                              tagName !== 'br' &&
                              tagName !== 'img' &&
                              element.textContent &&
                              element.textContent.trim().length > 0
                            ) {
                              return true;
                            }
                          }

                          // Fallback: check if there's any meaningful text content outside of headings
                          const textContent = tempDiv.textContent || tempDiv.innerText || '';
                          const tempDivForHeadings = document.createElement('div');
                          tempDivForHeadings.innerHTML = partData.content;
                          const headings = tempDivForHeadings.querySelectorAll('h1, h2, h3, h4, h5, h6');
                          let headingText = '';
                          headings.forEach(h => {
                            headingText += h.textContent || '';
                          });

                          const nonHeadingText = textContent.replace(headingText, '').trim();
                          return nonHeadingText.length > 0;

                        } catch (error) {
                          console.warn('Error parsing content for paragraph detection:', error);
                          // Default to showing modal on error to be safe
                          return true;
                        }
                      };

                      const shouldShowRewriteModal = hasContentWithParagraphs();

                      if (shouldShowRewriteModal) {
                        // Show rewrite modal for parts with paragraph content
                        setOpenPartRewrite(true);

                        setPartData((currentPartData) => {
                          const previousContent =
                            formikProps.values.content_rewrite?.[currentPartData?.title] ||
                            currentPartData?.content ||
                            "";

                          formikProps.setFieldValue("content_rewrite", {
                            ...formikProps.values.content_rewrite,
                            [currentPartData?.title]: cleaned,
                          });

                          return {
                            ...currentPartData,
                            previousContent,
                            newContent: cleaned,
                          };
                        });
                      } else {
                        // Insert directly into editor with typewriter effect for parts with only headings
                        setOpenPartModal(false);

                        setPartData((currentPartData) => {
                          try {
                            editor
                              .chain()
                              .focus()
                              .deleteRange({
                                from: currentPartData.startOfPart,
                                to: currentPartData.endOfPart,
                              })
                              .insertContentAt(currentPartData.startOfPart, cleaned)
                              .run();
                          } catch (error) {
                            // Editor insert failed
                          }

                          const updatedPartData = {
                            ...currentPartData,
                            content: cleaned,
                            startOfPart: currentPartData.startOfPart,
                            endOfPart: editor.state.selection.$from.end(2),
                          };

                          formikProps.setFieldValue("isPartGenerated", true);

                          return updatedPartData;
                        });
                      }
                    } else {
                      // Regular generation (not rewrite)
                      setOpenPartModal(false);

                      setPartData((currentPartData) => {
                        try {
                          editor
                            .chain()
                            .focus()
                            .deleteRange({
                              from: currentPartData.startOfPart,
                              to: currentPartData.endOfPart,
                            })
                            .insertContentAt(currentPartData.startOfPart, cleaned)
                            .run();
                        } catch (error) {
                          // Editor insert failed
                        }

                        const updatedPartData = {
                          ...currentPartData,
                          content: cleaned,
                          startOfPart: currentPartData.startOfPart,
                          endOfPart: editor.state.selection.$from.end(2),
                        };

                        formikProps.setFieldValue("isPartGenerated", true);

                        return updatedPartData;
                      });
                    }
                  }

                  else if (data.contentType === "conclusion") {
                    if (data.isRewrite) {
                      setOpenConclusionRewrite(true);
                      formikProps.setFieldValue(
                        "conclusion_rewrite",
                        cleanGeneratedContent(bufferRef.current)
                      );
                    } else {
                      // Close modal and update conclusion zone in real-time
                      setOpenConclusionModal(false);
                      const zone = document.getElementById("conclusionzone");
                      if (zone) {
                        zone.innerHTML = cleanGeneratedContent(
                          concluHeading.current + bufferRef.current
                        );
                      }
                    }
                  } else if (data.contentType === "custom") {
                    setOpenCustomModal(false);
                    const cleanedContent = cleanGeneratedContent(bufferRef.current);
                    if (cleanedContent && cleanedContent.length > 0) {
                      setCustomData((currentCustomData) => {
                        try {
                          editor
                            .chain()
                            .focus()
                            .deleteRange({
                              from: currentCustomData.startOfCustom,
                              to: currentCustomData.endOfCustom,
                            })
                            .insertContentAt(
                              currentCustomData.startOfCustom,
                              cleanedContent
                            )
                            .run();
                        } catch (error) {
                          // Error updating custom content
                        }

                        const updatedCustomData = {
                          ...currentCustomData,
                          startOfPart: currentCustomData.startOfCustom,
                          endOfCustom: editor.state.selection.$from.end(2),
                        };
                        return updatedCustomData;
                      });
                    }
                  }
                }
              } catch (error) {
                // Error rendering content
              }
            }
          } catch (parseError) {
            // Error parsing SSE message
          }
        };

      }
    } catch (error) {
      dispatch(setStreaming({ isStreaming: false }));
    }

    // Cleanup function - only runs when effect dependencies change or component unmounts
    return () => {
      if (socket) {
        socket.close();
        setWs(null);
        dispatch(setStreaming({ isStreaming: false }));
      }
    };
  }, [userInfo?.unique_id, formikProps.values.id]); // Only depend on the actual values we need

  return (
    <WebSocketContext.Provider
      value={{
        uploadingState,
        analysisState,
        editorRef,
        openOutlineRewrite,
        setOpenOutlineRewrite,
        openIntroductionModal,
        setOpenIntroductionModal,
        openIntroductionRewrite,
        setOpenIntroductionRewrite,
        partData,
        setPartData,
        openPartModal,
        setOpenPartModal,
        openPartRewrite,
        setOpenPartRewrite,
        customData,
        setCustomData,
        openCustomModal,
        setOpenCustomModal,
        openConclusionModal,
        setOpenConclusionModal,
        openConclusionRewrite,
        setOpenConclusionRewrite,
        introHeading,
        concluHeading,
        bufferRef,
        cleanGeneratedContent,
        openRephraseModal,
        setOpenRephraseModal,
        reconnectSSE,
        isConnecting,
        ws,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook for accessing WebSocket context
export const useWebSocket = () => useContext(WebSocketContext);
